// app/javascript/controllers/admin_message_tabs_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    currentMessageId: Number,
    // allow per-usage overrides via data-* values:
    storageKey: { type: String, default: "adminCurrentContactMessageId" },
    frameId:    { type: String, default: "contact_message_panel" },
    type:       { type: String, default: "" }
  }
  static targets = [ "list" ]

  connect() {
    const stored = Number(sessionStorage.getItem(this.storageKeyValue))
    if (!Number.isNaN(stored) && stored > 0) this.currentMessageIdValue = stored

    if (this.hasListTarget) {
      this.observer = new MutationObserver(() => this.deferSync())
      this.observer.observe(this.listTarget, { childList: true, subtree: true })
    }
    this.deferSync()
  }

  disconnect() { this.observer?.disconnect() }

  select(e) {
    if (e.button && e.button !== 0) return
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

    e.preventDefault()

    const link = e.currentTarget
    const row  = link.closest("[data-message-id]")
    const id   = Number(row?.dataset.messageId)

    if (!Number.isNaN(id)) {
      this.currentMessageIdValue = id
      sessionStorage.setItem(this.storageKeyValue, String(id))
      this.applyHighlight()
    }

    if (row) {
      const wasUnread = row.dataset.unread === "true"
      row.dataset.unread = "false"
      if (wasUnread) this.adjustTabUnreadCount(-1)

      row.classList.remove("bg-yellow-50", "border-l-yellow-700", "text-yellow-900", "font-semibold")
      const badge = row.querySelector('[data-role="message-badge"]')
      if (badge) {
        badge.classList.add("hidden")
        badge.style.display = "none"
      }
      this.updateRowAppearance(row)

      if (row.dataset.archived === "true") {
        row.classList.add("opacity-80")
      } else {
        row.classList.remove("opacity-80")
      }
    }

    const frame = link.dataset.frame || this.frameIdValue
    const url   = link.dataset.url
    if (url) {
      if (window.Turbo) {
        window.Turbo.visit(url, { frame })
      } else {
        window.location.assign(url)
      }
    }

    this.deferSync()
  }

  stop(e) {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.closest("form")?.requestSubmit()
  }

  onFrameLoad(e) {
    // only react to the configured frame
    if (!e?.target?.id) return
    const frame = this.frameIdValue
    if (e.target.id === frame) this.deferSync()
  }

  onStreamRender() { this.deferSync() }

  deferSync() {
    cancelAnimationFrame(this._raf || 0)
    this._raf = requestAnimationFrame(() => this.applyHighlight())
  }

  applyHighlight() {
    const rows = Array.from(this.element.querySelectorAll("[data-message-id]"))
    rows.forEach(r => {
      const isActive = Number(r.dataset.messageId) === this.currentMessageIdValue
      const isArchived = r.dataset.archived === "true"
      r.setAttribute("aria-selected", String(isActive))
      r.tabIndex = isActive ? 0 : -1

      if (isActive) {
        r.classList.add("border-l-slate-900", "bg-slate-50", "text-slate-900")
        r.classList.remove("border-l-transparent", "text-slate-700", "border-l-yellow-700", "opacity-80", "text-yellow-900", "font-semibold")
        r.classList.remove("bg-yellow-50")
      } else {
        r.classList.remove("border-l-slate-900", "bg-slate-50", "text-slate-900")
        if (r.dataset.unread === "true") {
          r.classList.add("border-l-yellow-700", "bg-yellow-50", "text-yellow-900", "font-semibold")
          r.classList.remove("border-l-transparent", "text-slate-700")
        } else {
          r.classList.remove("bg-yellow-50", "border-l-yellow-700", "text-yellow-900", "font-semibold", "bg-slate-50")
          r.classList.add("border-l-transparent", "text-slate-700")
          if (isArchived) {
            r.classList.add("opacity-80")
          } else {
            r.classList.remove("opacity-80")
          }
        }
      }
      this.updateRowAppearance(r)
    })
  }

  updateRowAppearance(row) {
    const unread = row.dataset.unread === "true"
    const title = row.querySelector('[data-role="message-title"]')
    if (title) {
      title.classList.toggle("text-yellow-900", unread)
      title.classList.toggle("text-slate-900", !unread)
    }

    const badge = row.querySelector('[data-role="message-badge"]')
    if (badge) {
      badge.classList.toggle("hidden", !unread)
      badge.style.display = unread ? "" : "none"
    }
  }

  adjustTabUnreadCount(delta) {
    if (!delta) return
    const type = this.typeValue
    if (!type) return

    const badge = document.querySelector(`[data-role="support-unread"][data-tab="${type}"]`)
    if (!badge) return

    const current = parseInt(badge.dataset.count || badge.textContent, 10) || 0
    const next = Math.max(0, current + delta)

    badge.dataset.count = String(next)

    if (next > 0) {
      badge.textContent = String(next)
      badge.classList.remove("hidden")
      badge.style.display = ""
    } else {
      badge.textContent = ""
      badge.classList.add("hidden")
      badge.style.display = "none"
    }

    const select = document.getElementById("support-tabs-select")
    if (!select) return

    Array.from(select.options).forEach(option => {
      if (option.dataset.tab !== type) return

      const baseLabel = option.dataset.label || option.textContent.trim().replace(/ \(\d+\)$/, "")
      option.dataset.label = baseLabel
      option.dataset.count = String(next)
      option.textContent = next > 0 ? `${baseLabel} (${next})` : baseLabel
    })
  }
}
